import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import SettlementService from '../../services/SettlementService';
import { customerService } from '../../services/CustomerService';
import SupplierService from '../../services/SupplierService';
import { useNavigate, useParams } from 'react-router-dom';

const SettlementEditPage = () => {
    const { id } = useParams();
    const [mounted, setMounted] = useState(false);
    const [customerId, setCustomerId] = useState(null);
    const [supplierId, setSupplierId] = useState(null);
    const [amount, setAmount] = useState(0);
    const [date, setDate] = useState(new Date());
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [customerDetail, setCustomerDetail] = useState(null);
    const [supplierDetail, setSupplierDetail] = useState(null);

    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
        loadSettlement();
    }, [id]);

    const loadSettlement = async () => {
        try {
            const data = await SettlementService.getSettlementById(id);
            setCustomerId(data.customerId);
            setSupplierId(data.supplierId);
            setAmount(data.amount);
            setDate(new Date(data.date));
            setDescription(data.description || '');
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Mahsuplaşma bilgileri yüklenemedi.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (customerId) {
            customerService.getCustomerDetail(customerId).then(res => {
                if (res.data.success) {
                    setCustomerDetail(res.data.data);
                }
            });
        }
    }, [customerId]);

    useEffect(() => {
        if (supplierId) {
            SupplierService.getSupplierDetail(supplierId).then(data => {
                setSupplierDetail(data);
            });
        }
    }, [supplierId]);

    const handleSave = async () => {
        if (amount <= 0) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen geçerli bir tutar giriniz.', life: 3000 });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                id: parseInt(id),
                amount,
                date,
                description
            };
            const res = await SettlementService.updateSettlement(payload);
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Mahsuplaşma işlemi başarıyla güncellendi.', life: 3000 });
                setTimeout(() => navigate('/finance/settlements/list'), 1500);
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message, life: 3000 });
                setIsSaving(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem sırasında bir hata oluştu.', life: 3000 });
            setIsSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center p-8"><i className="pi pi-spin pi-spinner text-4xl text-indigo-600" /></div>;

    return (
        <div className="max-w-5xl mx-auto p-2 animate-fade-in">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center text-white shadow-lg shadow-amber-200">
                        <i className="pi pi-pencil text-lg" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black text-slate-800 leading-tight">Mahsuplaşma Düzenle</h1>
                        <p className="text-xs text-slate-500">Kayıtlı mahsup işleminin detaylarını güncelleyin.</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        label="Geri Dön"
                        icon="pi pi-arrow-left"
                        className="p-button-text p-button-secondary p-button-sm font-bold"
                        onClick={() => navigate(-1)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-8 space-y-4">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Müşteri Hesabı</label>
                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex justify-between items-center opacity-70">
                                <span className="font-bold text-slate-700">{customerDetail?.customer?.name || 'Yükleniyor...'}</span>
                                <i className="pi pi-lock text-xs text-slate-400" />
                            </div>
                            <p className="text-[10px] text-slate-400 italic mt-1">* Mahsuplaşmada hesaplar değiştirilemez.</p>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Tedarikçi Hesabı</label>
                            <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex justify-between items-center opacity-70">
                                <span className="font-bold text-slate-700">{supplierDetail?.supplier?.name || 'Yükleniyor...'}</span>
                                <i className="pi pi-lock text-xs text-slate-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-3">İşlem Açıklaması & Notlar</label>
                        <InputTextarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            autoResize
                            className="w-full !rounded-xl border-slate-200 text-sm"
                            placeholder="İşlem notunu güncelleyin..."
                        />
                    </div>
                </div>

                <div className="md:col-span-4">
                    <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm h-full overflow-hidden flex flex-col">
                        <div className="p-6 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
                            <span className="text-[11px] font-black uppercase tracking-[0.2em] opacity-80 block mb-4">Mahsup Tutarı</span>
                            <div className="p-fluid">
                                <InputNumber
                                    value={amount}
                                    onValueChange={(e) => setAmount(e.value)}
                                    mode="currency"
                                    currency="TRY"
                                    locale="tr-TR"
                                    min={0}
                                    className="w-full"
                                    inputClassName="!bg-white/10 !border-white/20 !text-white !font-black !text-3xl !h-16 !rounded-xl placeholder:text-white/30 text-center"
                                />
                            </div>
                        </div>

                        <div className="p-6 space-y-6 flex-1 flex flex-col justify-between">
                            <div className="p-fluid">
                                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block mb-2">İşlem Tarihi</label>
                                <Calendar
                                    value={date}
                                    onChange={(e) => setDate(e.value)}
                                    showIcon
                                    className="w-full !rounded-xl"
                                    appendTo={mounted ? document.body : "self"}
                                />
                            </div>

                            <Button
                                label={isSaving ? "Güncelleniyor..." : "Değişiklikleri Kaydet"}
                                icon="pi pi-save"
                                className="!bg-indigo-600 !border-indigo-600 !h-14 !rounded-2xl !font-black !text-lg shadow-lg shadow-indigo-100"
                                onClick={handleSave}
                                loading={isSaving}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettlementEditPage;
